import React from 'react';
import { useIsKinderConnectCaptureEnabledForLeaves } from './use-is-kinder-connect-capture-enabled-for-leaves';
import { useKinderConnectCaptureModalWithContext } from './use-kinder-connect-capture-modal-with-context';
/**
 * Combines {@link useIsKinderConnectCaptureEnabledForLeaves} with the capture modal into the
 * "either open capture or proceed directly" gate that every leave/absence and holiday-reply
 * submission needs, so call sites don't each hand-roll the same steps.
 *
 * Unlike {@link useKinderConnectCaptureGate} (the check-in/check-out gate), there's no site-level
 * fallback here -- `childrenLeaves.kinderConnectCaptureRequired` answers on its own, so this hook
 * takes no `siteId`.
 *
 * @param childIds - The children the leave/absence is for. Pass a one-element array for a
 * single-child entry point.
 * @example
 * const {runOrCapture} = useKinderConnectCaptureGateForLeaves([childId]);
 * const handleSubmit = () => runOrCapture(kcData => addChildLeave({variables: {data: [data], kinderConnectMedia: kcData}}));
 */
export const useKinderConnectCaptureGateForLeaves = (childIds) => {
    const { isEnabled, isLoading } = useIsKinderConnectCaptureEnabledForLeaves(childIds);
    const kcCaptureModal = useKinderConnectCaptureModalWithContext();
    const pendingProceed = React.useRef(null);
    React.useEffect(() => {
        if (isLoading || pendingProceed.current === null) {
            return;
        }
        const queued = pendingProceed.current;
        pendingProceed.current = null;
        if (isEnabled) {
            kcCaptureModal.show({ onCapture: queued });
        }
        else {
            queued();
        }
    }, [isEnabled, isLoading, kcCaptureModal]);
    const runOrCapture = (onProceed) => {
        if (isLoading) {
            pendingProceed.current = onProceed;
            return undefined;
        }
        if (isEnabled) {
            kcCaptureModal.show({ onCapture: onProceed });
            return undefined;
        }
        return onProceed();
    };
    return { isEnabled, isLoading, runOrCapture };
};
